/**
 * Local boost/preference scores — the per-browser record of the user's own
 * Boost/Penalize nudges, and the helpers that fold that record into the
 * remotely shared ranking values.
 *
 * The record itself lives in src/settings.ts under `nitter:my-bias` — the
 * ONE inventory of `nitter:*` keys, deliberately shared by the dashboard and
 * the /view banner so a nudge made in either surface shows up in the other.
 * (boost.ts used to keep banner nudges in its own `nitter:bias` key, which
 * made the two records invisible to each other; getMyBias() migrates any
 * leftover data once and drops the legacy key.)
 *
 * Merge semantics — the server keeps one SHARED bias per domain (decaying
 * ×0.98 per poll, clamped to ±50, and the only value the /view resolver
 * sees). The local record is the user's PERSONAL net delta. Displaying the
 * two as a plain sum would double-count dashboard nudges (those write BOTH
 * the remote value and the local tally — a single +10 would show as +20), so
 * the merge uses a floor/ceiling rule instead:
 *
 *   - local > 0 (net boosted):    personal floor  → max(remote, local)
 *   - local < 0 (net penalized):  personal ceiling → min(remote, local)
 *   - local == 0:                 shared value unchanged
 *
 * That keeps every nudge load-bearing: server-side decay or other visitors'
 * adjustments can never drag the user's own view below what they boosted
 * (nor above what they penalized), while fresh visitors simply see the
 * shared value.
 */
import { addMyBias, getMyBias } from "@/settings";
// noinspection ES6PreferShortImport
import { BIAS_LIMIT, clamp, round1 } from "@shared/scoring";
import type { HostEntry } from "../types";

/** The domain's locally stored personal delta (0 when there is none). */
export function getBias(domain: string): number {
    const v = getMyBias()[domain];
    // noinspection SuspiciousTypeOfGuard
    return typeof v === "number" && isFinite(v) ? v : 0;
}

/** Adds delta to the domain's local record, persists it, returns the new total. */
export function adjustBias(domain: string, delta: number): number {
    return addMyBias(domain, delta);
}

/** The whole local record — { [domain]: personal net delta }. */
export function getAllBias(): Record<string, number> {
    return getMyBias();
}

/**
 * The user's personal view of a domain's priority: the shared (remote) bias
 * merged with the locally stored record via the floor/ceiling rule above,
 * clamped to the same ±50 range the server uses.
 */
export function mergeBias(domain: string, remoteBias: number): number {
    const local = getBias(domain);
    const merged =
        local > 0 ? Math.max(remoteBias, local)
        : local < 0 ? Math.min(remoteBias, local)
        : remoteBias;
    return round1(clamp(merged, -BIAS_LIMIT, BIAS_LIMIT));
}

/**
 * Fold the locally stored boost/preference score into a host entry: `bias`
 * becomes the personal merged value (see mergeBias) and `effective` is
 * recomputed from it — clamp(score + bias, 0, 100), the same formula the
 * server uses. Returns the SAME object when nothing changes, so callers
 * keep referential identity for unchanged rows.
 */
export function withLocalBias(host: HostEntry): HostEntry {
    const bias = mergeBias(host.domain, host.bias);
    if (bias === host.bias) return host;
    return {
        ...host,
        bias,
        effective: round1(clamp(host.score + bias, 0, 100)),
    };
}
